import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import {
  Search, FileText, ShoppingCart, CheckSquare,
  TrendingUp, Clock, AlertTriangle, Package,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    quotations: 0,
    orders: 0,
    pendingApprovals: 0,
    recentInquiries: 0,
  });
  const [recentQuotations, setRecentQuotations] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [qtRes, ordRes, appRes, inqRes] = await Promise.all([
          api.get("/quotations?limit=5"),
          api.get("/orders?limit=5"),
          api.get("/approvals?status=PENDING&limit=10"),
          api.get("/inquiry/analytics?days=7"),
        ]);
        setStats({
          quotations: qtRes.data.total || 0,
          orders: ordRes.data.total || 0,
          pendingApprovals: appRes.data.total || 0,
          recentInquiries: inqRes.data.total_searches || 0,
        });
        setRecentQuotations(qtRes.data.items || []);
        setRecentOrders(ordRes.data.items || []);
      } catch { /* fallback to 0 */ }
    };
    fetchData();
  }, []);

  const kpiCards = [
    { label: "Active Quotations", value: stats.quotations, icon: FileText, color: "#0052CC" },
    { label: "Sales Orders", value: stats.orders, icon: ShoppingCart, color: "#16A34A" },
    { label: "Pending Approvals", value: stats.pendingApprovals, icon: CheckSquare, color: "#F59E0B" },
    { label: "Inquiries (7d)", value: stats.recentInquiries, icon: Search, color: "#2563EB" },
  ];

  const quickActions = [
    { label: "Product Inquiry", icon: Search, to: "/inquiry", roles: ["Sales Agent", "Inquiry Department", "Admin"] },
    { label: "New Quotation", icon: FileText, to: "/quotations/new", roles: ["Sales Agent", "Admin"] },
    { label: "New Order", icon: ShoppingCart, to: "/orders/new", roles: ["Sales Agent", "Admin"] },
    { label: "Review Approvals", icon: CheckSquare, to: "/approvals", roles: ["Approver", "Branch Manager", "Admin"] },
  ];

  const userRoles = user?.roles || [];
  const hasRole = (roles) => roles.length === 0 || userRoles.includes("Admin") || roles.some((r) => userRoles.includes(r));

  const statusColors = {
    DRAFT: "bg-gray-100 text-gray-700",
    PENDING_APPROVAL: "bg-yellow-100 text-yellow-800",
    APPROVED: "bg-green-100 text-green-800",
    FOR_REVISION: "bg-orange-100 text-orange-800",
    CANCELLED: "bg-red-100 text-red-800",
  };

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">Welcome back, {user?.name}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="border border-gray-200 shadow-sm rounded-sm hover:shadow transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
                    {kpi.label}
                  </span>
                  <Icon size={18} style={{ color: kpi.color }} strokeWidth={1.5} />
                </div>
                <div className="text-3xl font-bold tracking-tight" style={{ color: kpi.color, fontFamily: "Chivo, sans-serif" }}>
                  {kpi.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border border-gray-200 shadow-sm rounded-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-gray-500 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.filter((a) => hasRole(a.roles)).map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.to}
                    data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
                    variant="outline"
                    onClick={() => navigate(action.to)}
                    className="h-auto py-4 px-4 justify-start gap-3 rounded-sm border-gray-200 hover:border-[#0052CC] hover:text-[#0052CC] transition-colors"
                  >
                    <Icon size={20} strokeWidth={1.5} />
                    <span className="text-sm font-medium">{action.label}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm rounded-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-gray-500 mb-4">Recent Quotations</h3>
            <div className="space-y-3">
              {recentQuotations.length === 0 && <p className="text-sm text-gray-400">No quotations yet</p>}
              {recentQuotations.slice(0, 5).map((q) => (
                <div
                  key={q.id}
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-sm transition-colors"
                  onClick={() => navigate(`/quotations/${q.id}`)}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-800">{q.number}</div>
                    <div className="text-xs text-gray-500">{q.customer_name}</div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${statusColors[q.status] || "bg-gray-100"}`}>
                    {q.status}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
